import React, { useMemo } from 'react';
import { Typography, Tooltip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WarningIcon from '@mui/icons-material/Warning';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import './JournalStats.css';

const JournalStats = ({ entries = [], statistics = {} }) => {
  // Calculate advanced metrics
  const advancedStats = useMemo(() => {
    if (!entries.length) return null;

    const wins = entries.filter(e => e.profit_loss > 0);
    const losses = entries.filter(e => e.profit_loss < 0);

    // Profit Factor
    const grossProfit = wins.reduce((sum, e) => sum + e.profit_loss, 0);
    const grossLoss = Math.abs(losses.reduce((sum, e) => sum + e.profit_loss, 0));
    const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0;

    // Average Win vs Average Loss
    const avgWin = wins.length > 0 ? grossProfit / wins.length : 0;
    const avgLoss = losses.length > 0 ? grossLoss / losses.length : 0;
    const winLossRatio = avgLoss > 0 ? avgWin / avgLoss : avgWin > 0 ? Infinity : 0;

    // Current Streak
    let currentStreak = 0;
    let streakType = null;
    const sortedEntries = [...entries].sort((a, b) =>
      new Date(b.trade_date) - new Date(a.trade_date)
    );

    for (const entry of sortedEntries) {
      const isWin = entry.profit_loss > 0;
      if (streakType === null) {
        streakType = isWin ? 'win' : 'loss';
        currentStreak = 1;
      } else if ((isWin && streakType === 'win') || (!isWin && streakType === 'loss')) {
        currentStreak++;
      } else {
        break;
      }
    }

    // Best/Worst Day of Week
    const dayStats = {};
    entries.forEach(entry => {
      const day = new Date(entry.trade_date).getDay();
      if (!dayStats[day]) dayStats[day] = { total: 0, count: 0 };
      dayStats[day].total += entry.profit_loss || 0;
      dayStats[day].count++;
    });

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    let bestDay = null;
    let worstDay = null;
    let bestAvg = -Infinity;
    let worstAvg = Infinity;

    Object.entries(dayStats).forEach(([day, stats]) => {
      const avg = stats.total / stats.count;
      if (avg > bestAvg) {
        bestAvg = avg;
        bestDay = dayNames[parseInt(day)];
      }
      if (avg < worstAvg) {
        worstAvg = avg;
        worstDay = dayNames[parseInt(day)];
      }
    });

    // Max Consecutive Wins/Losses
    let maxWinStreak = 0;
    let maxLossStreak = 0;
    let tempWinStreak = 0;
    let tempLossStreak = 0;

    sortedEntries.reverse().forEach(entry => {
      if (entry.profit_loss > 0) {
        tempWinStreak++;
        tempLossStreak = 0;
        maxWinStreak = Math.max(maxWinStreak, tempWinStreak);
      } else if (entry.profit_loss < 0) {
        tempLossStreak++;
        tempWinStreak = 0;
        maxLossStreak = Math.max(maxLossStreak, tempLossStreak);
      }
    });

    // Expectancy (Average P/L per trade)
    const expectancy = entries.reduce((sum, e) => sum + (e.profit_loss || 0), 0) / entries.length;

    return {
      profitFactor,
      avgWin,
      avgLoss,
      winLossRatio,
      currentStreak,
      streakType,
      bestDay,
      worstDay,
      maxWinStreak,
      maxLossStreak,
      expectancy,
      grossProfit,
      grossLoss
    };
  }, [entries]);

  const formatCurrency = (value) => {
    if (value === Infinity) return '∞';
    const absValue = Math.abs(value);
    if (absValue >= 1000) {
      return `$${(value / 1000).toFixed(1)}k`;
    }
    return `$${value.toFixed(2)}`;
  };

  const formatRatio = (value) => {
    if (value === Infinity) return '∞';
    return value.toFixed(2);
  };

  if (!advancedStats) {
    return (
      <div className="journal-stats empty">
        <Typography variant="body2" color="textSecondary">
          No data available
        </Typography>
      </div>
    );
  }

  return (
    <div className="journal-stats">
      {/* Primary Stats Row */}
      <div className="stats-row primary">
        <div className={`stat-card large ${(statistics?.total_profit_loss || 0) >= 0 ? 'positive' : 'negative'}`}>
          <div className="stat-icon">
            {(statistics?.total_profit_loss || 0) >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
          </div>
          <div className="stat-content">
            <span className="stat-label">Total P/L</span>
            <span className="stat-value">
              {(statistics?.total_profit_loss || 0) >= 0 ? '+' : ''}{formatCurrency(statistics?.total_profit_loss || 0)}
            </span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon neutral">
            <ShowChartIcon />
          </div>
          <div className="stat-content">
            <span className="stat-label">Win Rate</span>
            <span className="stat-value">{statistics?.win_ratio?.toFixed(1) || 0}%</span>
          </div>
        </div>

        <div className={`stat-card ${advancedStats.profitFactor >= 1 ? 'positive' : 'negative'}`}>
          <div className="stat-icon">
            <TimelineIcon />
          </div>
          <div className="stat-content">
            <span className="stat-label">Profit Factor</span>
            <span className="stat-value">{formatRatio(advancedStats.profitFactor)}</span>
          </div>
        </div>

        <div className={`stat-card ${advancedStats.expectancy >= 0 ? 'positive' : 'negative'}`}>
          <div className="stat-icon">
            <TrendingFlatIcon />
          </div>
          <div className="stat-content">
            <span className="stat-label">Expectancy</span>
            <span className="stat-value">
              {advancedStats.expectancy >= 0 ? '+' : ''}{formatCurrency(advancedStats.expectancy)}
            </span>
          </div>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="stats-row secondary">
        <Tooltip title="Current consecutive win or loss streak">
          <div className={`stat-card mini ${advancedStats.streakType === 'win' ? 'positive' : 'negative'}`}>
            <LocalFireDepartmentIcon className="stat-icon-inline" />
            <span className="stat-label">Streak</span>
            <span className="stat-value">
              {advancedStats.currentStreak} {advancedStats.streakType === 'win' ? 'W' : 'L'}
            </span>
          </div>
        </Tooltip>

        <Tooltip title="Average winning trade">
          <div className="stat-card mini positive">
            <span className="stat-label">Avg Win</span>
            <span className="stat-value">+{formatCurrency(advancedStats.avgWin)}</span>
          </div>
        </Tooltip>

        <Tooltip title="Average losing trade">
          <div className="stat-card mini negative">
            <span className="stat-label">Avg Loss</span>
            <span className="stat-value">-{formatCurrency(advancedStats.avgLoss)}</span>
          </div>
        </Tooltip>

        <Tooltip title="Average Win / Average Loss ratio">
          <div className={`stat-card mini ${advancedStats.winLossRatio >= 1 ? 'positive' : 'negative'}`}>
            <span className="stat-label">W/L Ratio</span>
            <span className="stat-value">{formatRatio(advancedStats.winLossRatio)}</span>
          </div>
        </Tooltip>

        <Tooltip title="Best performing day of week">
          <div className="stat-card mini positive">
            <EmojiEventsIcon className="stat-icon-inline" />
            <span className="stat-label">Best Day</span>
            <span className="stat-value">{advancedStats.bestDay || '-'}</span>
          </div>
        </Tooltip>

        <Tooltip title="Worst performing day of week">
          <div className="stat-card mini negative">
            <WarningIcon className="stat-icon-inline" />
            <span className="stat-label">Worst Day</span>
            <span className="stat-value">{advancedStats.worstDay || '-'}</span>
          </div>
        </Tooltip>

        <Tooltip title="Maximum consecutive winning trades">
          <div className="stat-card mini">
            <span className="stat-label">Max Win Streak</span>
            <span className="stat-value positive-text">{advancedStats.maxWinStreak}</span>
          </div>
        </Tooltip>

        <Tooltip title="Maximum consecutive losing trades">
          <div className="stat-card mini">
            <span className="stat-label">Max Loss Streak</span>
            <span className="stat-value negative-text">{advancedStats.maxLossStreak}</span>
          </div>
        </Tooltip>
      </div>

      {/* Gross Profit/Loss Bar */}
      <div className="gross-bar">
        <div className="gross-section profit" style={{ flex: advancedStats.grossProfit }}>
          <span className="gross-label">Gross Profit</span>
          <span className="gross-value">+{formatCurrency(advancedStats.grossProfit)}</span>
        </div>
        <div className="gross-section loss" style={{ flex: advancedStats.grossLoss }}>
          <span className="gross-label">Gross Loss</span>
          <span className="gross-value">-{formatCurrency(advancedStats.grossLoss)}</span>
        </div>
      </div>
    </div>
  );
};

export default JournalStats;
